/* eslint-disable @typescript-eslint/no-empty-function */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import algoliasearch from 'algoliasearch'
import { useDebounce } from 'use-debounce'
import Autosuggest, {
  InputProps,
  RenderSuggestionsContainerParams
} from 'react-autosuggest'
import clsx from 'clsx'

/* blocks */
import Link from '@/atoms/Link/Link'

/* utils */
import useOnClickOutside from '@/hooks/useOnClickOutside'
import useWindowSize from '@/hooks/useWindowSize'
import categories, { STANDALONE_CATEGORY_PAGES } from '@/utils/enums/categories'
import { dataLayerBlogSearchEvent } from '@/utils/functions/dataLayerEvents'

/* assets */
import { ReactComponent as SearchIcon } from 'public/assets/icons/search.svg'
import { ReactComponent as CloseIcon } from 'public/assets/icons/close.svg'

/* styling */
import styles from './_.module.css'

interface Props {
  onClose: () => void
  focusInput?: boolean
}

const DEBOUNCE_TIME = 500
const TIMEOUT_INTERVAL = 500

// TODO: replace `react-autosuggest` package
const AutoSuggestion = ({ focusInput, onClose }: Props) => {
  const router = useRouter()
  const { isDesktop } = useWindowSize()

  const [value, setValue] = useState<string>('')
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [suggestions, setSuggestions] = useState<SearchBlogPost[]>([])
  const [searchTerm] = useDebounce(value, DEBOUNCE_TIME)

  const ref = useRef<HTMLDivElement | null>(null)
  const autosuggestRef = useRef<Autosuggest<SearchBlogPost, null>>(null)

  useOnClickOutside(ref, () => handleSearchClose())

  const focusOnSearchInput = useCallback(() => {
    if (focusInput) {
      setTimeout(() => {
        autosuggestRef.current?.input?.focus()
      }, TIMEOUT_INTERVAL)
    }
  }, [focusInput])

  useEffect(() => {
    getSuggestionsFromAlgolia(searchTerm)
  }, [searchTerm])

  useEffect(() => {
    focusOnSearchInput()
  }, [focusOnSearchInput])

  useEffect(() => {
    if (!isDesktop && suggestions.length > 0) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
  }, [isDesktop, suggestions])

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-us', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  const getSuggestionsFromAlgolia = (value: string) => {
    if (value.length < 2) return

    dataLayerBlogSearchEvent({ searchTerm: value })
    const client = algoliasearch(
      '80V7J8FKYJ',
      '90c98e688a8e49e519db220456d83f84'
    )
    const index = client.initIndex(
      process.env.NEXT_PUBLIC_ALGOLIA_INDEX || 'prod_blog'
    )
    index.search(value, { hitsPerPage: 4 }).then(result => {
      setSuggestions(result.hits as SearchBlogPost[])
    })
  }

  const handleSearchClose = () => {
    setValue(() => '')
    setSuggestions([])
    setIsSearchFocused(false)
    onClose()
  }

  const showCloseIcon = isDesktop || suggestions.length > 0 || value.length > 0

  const inputProps: InputProps<SearchBlogPost> = {
    placeholder: 'Search...',
    value,
    onChange: (_, { newValue }) => setValue(newValue),
    onFocus: () => setIsSearchFocused(true),
    onKeyDown: e => {
      if (e.key === 'Enter') {
        router.push(`/blog/search?q=${value}`)
        setSuggestions([])
      }
    },
    className: clsx(
      styles.input,
      'text-paragraph-3  |  border border-blue-40 rounded-lg  |  w-full h-10  |  px-11'
    )
  }

  const renderSuggestionContainer = {
    ...(isSearchFocused && !isDesktop && value.length === 0
      ? {
          renderSuggestionsContainer: ({
            containerProps
          }: RenderSuggestionsContainerParams) => (
            <div {...containerProps}>
              <CategorySuggestions handleSearchClose={handleSearchClose} />
            </div>
          )
        }
      : {
          renderSuggestionsContainer: ({
            containerProps,
            children,
            query
          }: RenderSuggestionsContainerParams) => {
            if (query.length === 0) return null
            return (
              <div {...containerProps}>
                {children}
                {suggestions.length > 0 && (
                  <Link
                    href={`/blog/search/?q=${value}`}
                    className="text-caption font-medium text-blue-50 uppercase tracking-wider  |  inline-block  |  px-4 mb-4"
                  >
                    View all results
                  </Link>
                )}
              </div>
            )
          }
        })
  }

  return (
    <div
      className="flex items-center relative | max-w-col-8 mx-auto h-full"
      ref={ref}
    >
      <SearchIcon
        className={clsx(
          'text-blue-50  |  flex items-center  |  absolute left-4  |  z-10'
        )}
      />
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <Autosuggest
        ref={autosuggestRef}
        theme={styles}
        suggestions={suggestions}
        inputProps={inputProps}
        getSuggestionValue={() => ''}
        alwaysRenderSuggestions={true}
        onSuggestionsFetchRequested={() => {}}
        onSuggestionSelected={(_, data) => {
          router.push(`/blog/${data.suggestion.slug}`)
          document.body.style.overflow = 'auto'
        }}
        {...renderSuggestionContainer}
        renderSuggestion={suggestion => (
          <div
            className={clsx(
              styles.blogCard,
              'flex flex-col sm:flex-row sm:items-center  |  p-4  |  cursor-pointer'
            )}
          >
            {suggestion.featuredImage && (
              <img
                className="flex-shrink-0 w-full  |  rounded  |  object-cover  |  mb-2 sm:mb-0 sm:mr-6"
                src={suggestion.featuredImage.thumbnail}
                alt={suggestion.title}
                width="150"
                height="80"
              />
            )}
            <div className="text-left">
              <p className="text-paragraph-4 font-medium  |  mb-1">
                {suggestion.title}
              </p>
              <p className="text-caption text-gray-60">
                {formatDate(suggestion.date)}
              </p>
            </div>
          </div>
        )}
      />

      <button
        onClick={handleSearchClose}
        className={clsx(
          'flex items-center  |  absolute right-4  |  text-gray-70  |  z-10  |  p-2',
          showCloseIcon ? 'block' : 'hidden'
        )}
      >
        {value && <CloseIcon className="h-4 w-4" />}
      </button>
    </div>
  )
}

const CategorySuggestions = ({
  handleSearchClose
}: {
  handleSearchClose: () => void
}) => {
  return (
    <div className={styles.categorySuggestionsContainer}>
      <p className="text-caption uppercase text-gray-70 font-medium |  mb-3">
        Categories
      </p>
      {Object.values(categories).map(category => {
        if (!category || !category.isFeatured) return
        const linkPrefix = category.slug.startsWith('http')
          ? ''
          : STANDALONE_CATEGORY_PAGES.includes(category.slug)
          ? '/'
          : '/blog/category/'

        return (
          <Link
            key={category.slug}
            href={`${linkPrefix}${category.slug}/`}
            className="block  |  mb-2"
            onClick={handleSearchClose}
          >
            {category.name}
          </Link>
        )
      })}
    </div>
  )
}

export default AutoSuggestion
